<template>
  <div class="landing-page">
    <nav class="navbar">
      <div class="navbar-container">
        <div class="navbar-brand" @click="goHome">
          <img
            :src="logo"
            :style="{ height: 'var(--logo-height-login)', width: 'var(--logo-width-login)' }"
            alt="MedAn Logo"
          />
        </div>

        <ul class="navbar-menu">
          <li
            v-for="nav in navSections"
            :key="nav.section_key"
          >
            <a
              href="#"
              class="nav-link"
              :class="{ 'nav-link--active': currentSection === nav.section_key }"
              @click.prevent="showSection(nav.section_key)"
            >
              {{ nav.nav_label }}
            </a>
          </li>
        </ul>

        <div class="navbar-actions">
          <button class="btn-login" @click="handleLogin">
            Log In
          </button>
          <button class="btn-demo" @click="handleRequestDemo">
            Request Demo
          </button>
        </div>

        <button class="hamburger" @click="toggleMobileMenu" aria-label="Toggle menu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <div class="mobile-menu" :class="{ open: mobileMenuOpen }">
        <a
          v-for="nav in navSections"
          :key="nav.section_key"
          href="#"
          class="mobile-nav-link"
          :class="{ 'mobile-nav-link--active': currentSection === nav.section_key }"
          @click.prevent="showSection(nav.section_key)"
        >
          {{ nav.nav_label }}
        </a>
        <div class="mobile-actions">
          <button class="btn-login" @click="handleLogin">
            Log In
          </button>
          <button class="btn-demo" @click="handleRequestDemo">
            Request Demo
          </button>
        </div>
      </div>
    </nav>

    <section v-if="currentSection !== 'contact'" :class="sectionClass">
      <div v-if="sectionsLoading" class="section-loading">
        <div class="loading-spinner"></div>
      </div>

      <div v-else-if="sectionsError" class="section-error">
        <i class="mdi mdi-alert-circle-outline"></i>
        <p>{{ sectionsError }}</p>
        <button @click="fetchLandingSections">
          Try Again
        </button>
      </div>

      <template v-else-if="getSectionByKey(currentSection)">
        <div v-if="currentSection === 'homepage'" class="homepage-container">
          <h1 class="homepage-title">
            {{ getSectionByKey('homepage').title }}
          </h1>
          <p class="homepage-description">
            {{ getSectionByKey('homepage').description }}
          </p>
          <div class="homepage-actions">
            <button class="btn-start-demo" @click="handleStartDemo">
              Start Demo &nbsp;→
            </button>
            <button class="btn-learn-more" @click="handleLearnMore">
              Learn More
            </button>
          </div>
        </div>

        <div v-else-if="currentSection === 'product'" class="product-container">
          <h4 class="product-subtitle">
            {{ getSectionByKey('product').subtitle }}
          </h4>
          <h2 class="product-title">
            {{ getSectionByKey('product').title }}
          </h2>
          <p class="product-description">
            {{ getSectionByKey('product').description }}
          </p>
        </div>

        <template v-else-if="currentSection === 'blog'">
          <template v-if="!blogDetailOpen">
            <div class="blog-container">
              <h4 class="blog-subtitle">
                {{ getSectionByKey('blog').subtitle }}
              </h4>
              <h2 class="blog-title">
                {{ getSectionByKey('blog').title }}
              </h2>

              <div class="blog-grid">
                <a
                  v-for="item in blogItems"
                  :key="item.id"
                  class="blog-card"
                  :class="{ 'blog-card--disabled': !item.image && !item.pdf && !item.pdf_content }"
                  href="#"
                  @click.prevent="(item.image || item.pdf || item.pdf_content) ? openBlogDetail(item) : null"
                >
                  <div class="blog-card-image">
                    <img
                      v-if="item.image"
                      :src="`${baseURL}/${item.image}`"
                      :alt="item.title"
                    />
                    <div v-else class="blog-card-image--placeholder">
                      {{ (!item.image && !item.pdf && !item.pdf_content) ? 'Blog Coming Soon' : 'Image Coming Soon' }}
                    </div>
                  </div>
                  <div class="blog-card-date">
                    {{ formatBlogDate(item.created_at) }}
                  </div>
                  <h3 class="blog-card-title">
                    {{ (!item.image && !item.pdf && !item.pdf_content) ? 'Blog Coming Soon' : item.title }}
                  </h3>
                </a>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="blog-detail">
              <div class="blog-detail-container">
                <div class="blog-breadcrumb">
                  <i class="mdi mdi-home-outline" @click="closeBlogDetail"></i>
                  <i class="mdi mdi-chevron-right"></i>
                  <span @click="closeBlogDetail">
                    Posts
                  </span>
                  <i class="mdi mdi-chevron-right"></i>
                  <span class="blog-breadcrumb-current">
                    {{ truncateTitle(selectedBlogItem.title) }}
                  </span>
                </div>

                <h1 class="blog-detail-title">
                  {{ selectedBlogItem.title }}
                </h1>
                <p class="blog-detail-subtitle">
                  {{ selectedBlogItem.text }}
                </p>

                <div class="blog-detail-meta">
                  <span>Published on {{ formatBlogDate(selectedBlogItem.created_at) }}</span>
                  <span class="blog-detail-meta-dot">•</span>
                  <span>Updated {{ formatBlogDate(selectedBlogItem.updated_at) }}</span>
                </div>

                <div class="blog-detail-hero">
                  <div class="blog-detail-author">
                    <div class="blog-detail-avatar">
                      <img
                        v-if="selectedBlogItem.icon"
                        :src="`${baseURL}/${selectedBlogItem.icon}`"
                        alt="author"
                      />
                      <v-icon v-else size="108" color="#a2a9b0">
                        mdi-account-circle
                      </v-icon>
                    </div>
                    <div class="blog-detail-author-name">
                      {{ selectedBlogItem.author_name }}
                    </div>
                  </div>

                  <div class="blog-detail-image">
                    <img
                      v-if="selectedBlogItem.image"
                      :src="`${baseURL}/${selectedBlogItem.image}`"
                      :alt="selectedBlogItem.title"
                    />
                    <div v-else class="blog-card-image--placeholder">
                      Image Coming Soon
                    </div>
                  </div>
                </div>

                <div class="blog-detail-body" v-if="blogSections.length">
                  <aside class="blog-detail-toc">
                    <div class="blog-detail-toc-label">
                      Table of Contents
                    </div>
                    <ul>
                      <li
                        v-for="(section, index) in blogSections"
                        :key="index"
                        :class="{ active: activeBlogSectionIndex === index }"
                        @click="activeBlogSectionIndex = index"
                      >
                        {{ section.label }}
                      </li>
                    </ul>
                  </aside>

                  <div class="blog-detail-content">
                    <div
                      v-for="(paragraph, pIndex) in blogSections[activeBlogSectionIndex].paragraphs"
                      :key="pIndex"
                      v-html="renderBlogParagraph(paragraph)"
                    ></div>
                  </div>
                </div>

                <div class="blog-detail-body" v-else>
                  <div class="blog-detail-content">
                    <p>{{ selectedBlogItem.text || 'Content coming soon.' }}</p>
                  </div>
                </div>
              </div>

              <button
                class="edit-blog-floating"
                type="button"
                @click="requestEditBlog(selectedBlogItem)"
              >
                <i class="mdi mdi-pencil-outline"></i>
                <span>Edit with MedAn Blog</span>
              </button>
            </div>
          </template>

          <BlogAuthVerifyDialog
            v-model="blogVerifyDialogOpen"
            :required-author="pendingEditItem?.author_name"
            @verified="onBlogVerified"
          />

          <BlogEditorDialog
            v-model="blogEditDialog"
            mode="edit"
            :saving="blogEditorSaving"
            :toolbar="blogEditorToolbar"
            :initial-data="blogEditDialogInitialData"
            @image-added="handleInlineImageUpload"
            @toast="({ message, type }) => showToast(message, type)"
            @save="saveBlogEdit"
            @close="closeBlogEditor"
          />
        </template>

        <div v-else-if="currentSection === 'solutions'" class="solutions-container">
          <h4 class="solutions-subtitle">
            {{ getSectionByKey('solutions').subtitle }}
          </h4>
          <h2 class="solutions-title">
            {{ getSectionByKey('solutions').title }}
          </h2>
          <div class="solutions-grid">
            <div
              v-for="item in getRootItems(getSectionByKey('solutions'))"
              :key="item.id"
              class="solution-item"
            >
              <div class="solution-icon">
                <v-icon size="48" color="#C1C7CD">
                  {{ item.icon }}
                </v-icon>
              </div>
              <h3>{{ item.title }}</h3>
            </div>
          </div>
        </div>

        <div v-else-if="currentSection === 'technology'" class="technology-container">
          <h4 class="technology-subtitle">
            {{ getSectionByKey('technology').subtitle }}
          </h4>
          <h2 class="technology-title">
            {{ getSectionByKey('technology').title }}
          </h2>
          <p class="technology-description">
            {{ getSectionByKey('technology').description }}
          </p>
        </div>

        <div v-else-if="currentSection === 'about'" class="about-container">
          <h3 class="about-subtitle">
            {{ getSectionByKey('about').subtitle }}
          </h3>
          <h2 class="about-title">
            {{ getSectionByKey('about').title }}
          </h2>
          <p class="about-description">
            {{ getSectionByKey('about').description }}
          </p>
        </div>

        <template v-if="!(currentSection === 'blog' && blogDetailOpen)">
          <template v-for="block in landingBlocks">
            <div v-if="block.type === 'overview'" :key="block.id" class="product-overview-section">
              <h4 class="product-overview-subtitle">
                {{ block.title }}
              </h4>
              <h2 class="product-overview-title">
                {{ block.text }}
              </h2>

              <div class="product-overview-wrapper">
                <button
                  v-if="block.images.length > 1"
                  class="newsletter-arrow newsletter-arrow--prev"
                  :class="{ 'newsletter-arrow--disabled': getActiveImageIndex(block.id) === 0 }"
                  :disabled="getActiveImageIndex(block.id) === 0"
                  @click="prevProductImage(block)"
                  aria-label="Previous"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>

                <div class="product-overview-placeholder">
                  <img
                    v-if="block.images[getActiveImageIndex(block.id)]"
                    :src="`${baseURL}/${block.images[getActiveImageIndex(block.id)]}`"
                    class="product-overview-image"
                    :alt="block.title"
                  />
                  <span v-else>
                    Product Preview Coming Soon
                  </span>
                </div>

                <button
                  v-if="block.images.length > 1"
                  class="newsletter-arrow newsletter-arrow--next"
                  :class="{ 'newsletter-arrow--disabled': getActiveImageIndex(block.id) === block.images.length - 1 }"
                  :disabled="getActiveImageIndex(block.id) === block.images.length - 1"
                  @click="nextProductImage(block)"
                  aria-label="Next"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>

              <div v-if="block.images.length > 1" class="product-overview-dots">
                <div
                  v-for="(img, dotIdx) in block.images"
                  :key="'dot-' + dotIdx"
                  class="product-overview-dot"
                  :class="{ active: getActiveImageIndex(block.id) === dotIdx }"
                  @click="setActiveImageIndex(block.id, dotIdx)"
                ></div>
              </div>
            </div>

            <div v-else-if="block.type === 'why'" :key="block.id" class="homepage-why-section">
              <h4 class="homepage-why-subtitle">
                {{ block.title }}
              </h4>
              <h2 class="homepage-why-title">
                {{ block.text }}
              </h2>
              <div class="homepage-why-grid">
                <div v-for="item in block.children" :key="item.id" class="homepage-why-item">
                  <i :class="`mdi ${item.icon} homepage-why-icon`"></i>
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.text }}</p>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'technology'" :key="block.id" class="technology-feature-section">
              <h4 class="technology-feature-subtitle">
                {{ block.title }}
              </h4>
              <h2 class="technology-feature-title">
                {{ block.text }}
              </h2>
              <div class="technology-feature-content">
                <div class="technology-feature-preview">
                  <img v-if="block.image" :src="`${baseURL}/${block.image}`" alt="Technology Feature Preview" />
                  <span v-else>
                    Product Preview Coming Soon
                  </span>
                </div>
                <div class="technology-feature-grid">
                  <div
                    v-for="item in block.children"
                    :key="item.id"
                    class="technology-feature-item"
                  >
                    <div class="technology-feature-head">
                      <i :class="`mdi ${item.icon} feature-tech-icon`"></i>
                      <h5 v-if="item.title" class="technology-feature-item-title">
                        {{ item.title }}
                      </h5>
                    </div>
                    <p>{{ item.text }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'features'" :key="block.id" class="features-section">
              <h4 class="features-subtitle">
                {{ block.title }}
              </h4>
              <h2 class="features-title">
                {{ block.text }}
              </h2>
              <div class="features-content">
                <div class="features-preview">
                  <img
                    v-if="block.image"
                    :src="`${baseURL}/${block.image}`"
                    class="features-preview-image"
                    :alt="block.title"
                  />
                  <span v-else>
                    Product Preview Coming Soon
                  </span>
                </div>
                <div class="features-grid">
                  <div v-for="item in block.children" :key="item.id" class="feature-item">
                    <i :class="`mdi ${item.icon} feature-icon`"></i>
                    <p class="feature-text">
                      {{ item.text }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'newsletter'" :key="block.id" class="newsletter-section">
              <div class="newsletter-container">
                <h4 class="newsletter-subtitle">
                  {{ block.title }}
                </h4>
                <h2 class="newsletter-title">
                  {{ block.text }}
                </h2>

                <div class="newsletter-slider-wrapper">
                  <button
                    v-if="hasNewsletterArrows"
                    class="newsletter-arrow newsletter-arrow--prev"
                    :class="{ 'newsletter-arrow--disabled': !canScrollPrev }"
                    :disabled="!canScrollPrev"
                    @click="scrollNewsletter('prev')"
                    aria-label="Previous"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>

                  <div ref="newsletterGrid" class="newsletter-grid" @scroll="onNewsletterScroll">
                    <div
                      v-for="item in block.children"
                      :key="item.id"
                      ref="newsletterCards"
                      class="newsletter-card"
                      :class="{ 'newsletter-card--disabled': !item.pdf }"
                      @click.prevent="item.pdf ? viewNewsletter(item) : null"
                    >
                      <div class="newsletter-image">
                        <img v-if="item.image" :src="`${baseURL}/${item.image}`" :alt="item.title" />
                        <span v-else>
                          {{ (!item.image && !item.pdf) ? 'Newsletter Coming Soon' : 'Newsletter Image Coming Soon' }}
                        </span>
                      </div>
                      <h3 class="newsletter-card-title">
                        {{ (!item.image && !item.pdf) ? 'Newsletter Coming Soon' : item.title }}
                      </h3>
                      <a
                        class="newsletter-readmore"
                        :class="{ 'newsletter-readmore--disabled': !item.pdf }"
                        @click.prevent="item.pdf ? viewNewsletter(item) : null"
                        href="#"
                      >
                        Read More
                      </a>
                    </div>
                  </div>

                  <button
                    v-if="hasNewsletterArrows"
                    class="newsletter-arrow newsletter-arrow--next"
                    :class="{ 'newsletter-arrow--disabled': !canScrollNext }"
                    :disabled="!canScrollNext"
                    @click="scrollNewsletter('next')"
                    aria-label="Next"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'team'" :key="block.id" class="team-section">
              <h3 class="team-subtitle">
                {{ block.title }}
              </h3>
              <h2 class="team-title">
                {{ block.text }}
              </h2>
              <div class="team-grid">
                <div
                  v-for="item in block.children"
                  :key="item.id"
                  class="team-card"
                >
                  <img
                    v-if="item.image"
                    :src="`${baseURL}/${item.image}`"
                    :alt="item.title"
                    class="team-image"
                  />
                  <div v-else class="team-image-placeholder">
                    Photo Coming Soon
                  </div>
                  <h4 class="team-name">
                    {{ item.title }}
                  </h4>
                  <p class="team-role">
                    {{ item.role }}
                  </p>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'faq'" :key="block.id" class="faq-section">
              <h4 class="faq-subtitle">
                {{ block.title }}
              </h4>
              <h2 class="faq-title">
                {{ block.text }}
              </h2>
              <div class="faq-container">
                <div
                  v-for="(item, index) in block.children"
                  :key="item.id"
                  class="faq-item"
                  :class="{ 'faq-item-active': activeFaq === `${block.id}-${index}` }"
                  @click="activeFaq = activeFaq === `${block.id}-${index}` ? null : `${block.id}-${index}`"
                >
                  <div class="faq-header">
                    <span>{{ item.title }}</span>
                    <span class="faq-icon">
                      {{ activeFaq === `${block.id}-${index}` ? '−' : '+' }}
                    </span>
                  </div>
                  <div class="faq-content" v-show="activeFaq === `${block.id}-${index}`">
                    {{ item.text }}
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="block.type === 'experience'" :key="block.id" class="experience-section">
              <div class="experience-container">
                <h2 class="experience-title">
                  <span class="experience-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="#FFC83D" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L13.8 8.2L20 10L13.8 11.8L12 18L10.2 11.8L4 10L10.2 8.2L12 2ZM5 15L5.9 18.1L9 19L5.9 19.9L5 23L4.1 19.9L1 19L4.1 18.1L5 15ZM19 13L19.9 16.1L23 17L19.9 17.9L19 21L18.1 17.9L15 17L18.1 16.1L19 13Z"/>
                    </svg>
                  </span>
                  {{ block.text }}
                </h2>
                <button class="experience-btn" @click="handleStartDemo">
                  Start Demo
                  <i class="mdi mdi-arrow-right"></i>
                </button>
              </div>
            </div>
          </template>
        </template>

        <v-dialog
          v-model="dialogViewPDF"
          max-width="90%"
          @keydown.esc="dialogViewPDF = false"
        >
          <v-card
            class="pa-4"
            style="height: 90vh; width: 100%; display: flex; flex-direction: column;"
            rounded="xl"
            elevation="10"
          >
            <div class="d-flex justify-space-between align-center mb-2">
              <h3 class="text-h6 ml-1 mb-0">
                {{ selectedTitlePDF }}
              </h3>
              <v-btn icon @click="dialogViewPDF = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <iframe
              v-if="selectedPDF"
              :src="selectedPDF"
              style="flex: 1; width: 100%; border: none; border-radius: 8px;"
            ></iframe>
          </v-card>
        </v-dialog>
      </template>
    </section>

    <section class="contact-section" v-if="currentSection === 'contact'">
      <div v-if="sectionsLoading" class="section-loading">
        <div class="loading-spinner"></div>
      </div>

      <div v-else-if="sectionsError" class="section-error">
        <i class="mdi mdi-alert-circle-outline"></i>
        <p>{{ sectionsError }}</p>
        <button @click="fetchLandingSections">
          Try Again
        </button>
      </div>

      <template v-else>
        <div class="contact-banner">
          <div class="contact-banner-blob contact-banner-blob--left-sm"></div>
          <div class="contact-banner-blob contact-banner-blob--left"></div>
          <div class="contact-banner-blob contact-banner-blob--right-sm"></div>
          <div class="contact-banner-blob contact-banner-blob--right-lg"></div>
          <div class="contact-banner-content">
            <h2 class="contact-banner-title">
              {{ getSectionByKey('contact').title }}
            </h2>
            <p class="contact-banner-description">
              {{ getSectionByKey('contact').description }}
            </p>
          </div>
        </div>

        <div class="contact-card">
          <div class="contact-form">
            <div
              class="form-group"
              v-for="field in getSectionByKey('contact').items.filter(i => i.icon !== null && i.icon !== 'COMPANY_SIZE' && i.icon !== 'INDUSTRY')"
              :key="field.id"
            >
              <label class="form-label">{{ field.title }} <span class="required">*</span></label>
              <input
                v-model="form[field.icon]"
                :type="field.icon === 'email' ? 'email' : 'text'"
                class="form-input"
                :placeholder="field.text"
              />
            </div>

            <div class="form-row">
              <div
                class="form-group"
                v-if="getSectionByKey('contact')?.items.find(i => (i.icon === null || i.icon === '') && i.title === 'COMPANY_SIZE')"
              >
                <label class="form-label">
                  {{ getSectionByKey('contact').items.find(i => (i.icon === null || i.icon === '') && i.title === 'COMPANY_SIZE')?.text }}
                  <span class="required">*</span>
                </label>
                <div class="select-wrapper">
                  <select v-model="form.companySize" class="form-select">
                    <option
                      v-for="opt in getSectionByKey('contact').items.filter(i => i.icon === 'COMPANY_SIZE')"
                      :key="opt.id"
                      :value="opt.text"
                    >
                      {{ opt.title }}
                    </option>
                  </select>
                  <span class="select-arrow">▾</span>
                </div>
              </div>

              <div
                class="form-group"
                v-if="getSectionByKey('contact')?.items.find(i => (i.icon === null || i.icon === '') && i.title === 'INDUSTRY')"
              >
                <label class="form-label">
                  {{ getSectionByKey('contact').items.find(i => (i.icon === null || i.icon === '') && i.title === 'INDUSTRY')?.text }}
                  <span class="required">*</span>
                </label>
                <div class="select-wrapper">
                  <select v-model="form.industry" class="form-select">
                    <option
                      v-for="opt in getSectionByKey('contact').items.filter(i => i.icon === 'INDUSTRY')"
                      :key="opt.id"
                      :value="opt.text"
                    >
                      {{ opt.title }}
                    </option>
                  </select>
                  <span class="select-arrow">▾</span>
                </div>
              </div>
            </div>

            <button class="btn-submit" @click="handleSubmit">
              Submit
            </button>
          </div>
        </div>
      </template>
    </section>

    <footer class="site-footer" v-if="getSectionByKey('footer')">
      <div class="footer-container">
        <div class="footer-brand">
          <div class="footer-logo">
            <img src="MedAn247-logo-dark.png" alt="MedAn Logo" class="footer-logo-img" />
          </div>
          <p class="footer-copyright">
            {{ getSectionByKey('footer').subtitle }}
          </p>
          <div class="footer-socials">
            <a
              v-for="social in getSectionByKey('footer').items.filter(i => !['COMPANY','SUPPORT','GROUP_LABEL'].includes(i.icon))"
              :key="social.id"
              :href="social.text"
              class="footer-social-link"
              :aria-label="social.title"
            >
              <i
                :class="social.icon.startsWith('mdi-') ? `mdi ${social.icon}` : social.icon"
                style="font-size: 18px;"
              ></i>
            </a>
          </div>
        </div>

        <div class="footer-col">
          <h4 class="footer-col-title">
            {{ getSectionByKey('footer').items.find(i => i.icon === 'GROUP_LABEL' && i.text === 'COMPANY')?.title }}
          </h4>
          <ul class="footer-links">
            <li
              v-for="item in getSectionByKey('footer').items.filter(i => i.icon === 'COMPANY')"
              :key="item.id"
            >
              <a :href="item.text" class="footer-link">
                {{ item.title }}
              </a>
            </li>
          </ul>
        </div>

        <div class="footer-col">
          <h4 class="footer-col-title">
            {{ getSectionByKey('footer').items.find(i => i.icon === 'GROUP_LABEL' && i.text === 'SUPPORT')?.title }}
          </h4>
          <ul class="footer-links">
            <li
              v-for="item in getSectionByKey('footer').items.filter(i => i.icon === 'SUPPORT')"
              :key="item.id"
            >
              <a :href="item.text" class="footer-link">
                {{ item.title }}
              </a>
            </li>
          </ul>
        </div>

        <div class="footer-col footer-stay-up-to-date">
          <h4 class="footer-col-title">
            {{ getSectionByKey('footer').title }}
          </h4>
          <div class="footer-email-wrap">
            <input
              type="email"
              class="footer-email-input"
              :placeholder="getSectionByKey('footer').description"
            />
            <button class="footer-email-btn" aria-label="Subscribe">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" stroke="none"/></svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';
import runtimeConfig from '../configRuntime.js';
import BlogAuthVerifyDialog from '@/components/BlogAuthVerifyDialog';
import BlogEditorDialog from '@/components/BlogEditorDialog';

export default {
  components: {
    BlogAuthVerifyDialog,
    BlogEditorDialog
  },
  name: 'LandingPage',
  data() {
    return {
      blogDetailOpen: false,
      selectedBlogItem: null,
      activeBlogSectionIndex: 0,
      blogVerifyDialogOpen: false,
      pendingEditItem: null,
      blogEditToken: null,
      blogEditDialog: false,
      blogEditorSaving: false,
      blogEditDialogInitialData: {},
      blogEditorToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ header: [1, 2, 3, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image"],
        ["clean"]
      ],
      logo: null,
      navSections: [],
      mobileMenuOpen: false,
      currentSection: 'homepage',
      sectionsLoading: false,
      sectionsError: null,
      landingSections: [],
      activeOverviewImageIndex: {},
      baseURL: runtimeConfig.VUE_APP_API_SOCKET,
      activeFaq: 0,
      canScrollPrev: false,
      canScrollNext: false,
      dialogViewPDF: false,
      selectedPDF: null,
      selectedTitlePDF: '',
      form: {
        fullName: '',
        email: '',
        companyName: '',
        jobTitle: '',
        companySize: '1-10',
        industry: 'Marketing',
      },
    }
  },
  async mounted() {
    await this.loadAssetsTheme();
    await this.fetchNavSections();
    await this.fetchLandingSections();
    this.$nextTick(() => {
      if (this.$refs.newsletterGrid) {
        this.updateArrowState();
      }
    });
  },
  watch: {
    currentSection(val) {
      const dynamicSections = ['homepage', 'product', 'solutions', 'technology', 'blog', 'about', 'contact'];
      if (dynamicSections.includes(val) && !this.landingSections.length) {
        this.fetchLandingSections();
      }
    },
    newsletterItems() {
      this.$nextTick(() => {
        this.updateArrowState();
      });
    },
    selectedBlogItem() {
      this.activeBlogSectionIndex = 0;
    }
  },
  computed: {
    sectionClass() {
      return this.currentSection === 'homepage' ? 'homepage' : `${this.currentSection}-section`;
    },
    landingBlocks() {
      const section = this.getSectionByKey(this.currentSection);
      if (!section) return [];

      return section.items
        .filter(i => i.block_item_id === null && i.is_active !== 0)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(block => {
          const children = section.items
            .filter(c => c.block_item_id === block.id && c.is_active !== 0)
            .sort((a, b) => a.sort_order - b.sort_order);

          return {
            ...block,
            type: this.getBlockVariant(section, block, children),
            children,
            images: this.parseBlockImages(block.image),
          };
        });
    },
    newsletterItems() {
      const items = this.getChildItems(this.getSectionByKey('homepage'), 'NEWSLETTER') || [];

      return [...items].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    },
    hasNewsletterArrows() {
      return this.newsletterItems.length > 3;
    },
    visibleCardNewsletterCount() {
      return 3;
    },
    blogSections() {
      const raw = this.selectedBlogItem?.pdf_content;
      if (!raw) return [];

      if (/###SECTION:.+?###/.test(raw)) {
        const parts = raw.split(/###SECTION:(.+?)###\n?/);
        const sections = [];

        for (let i = 1; i < parts.length; i += 2) {
          const label = parts[i]?.trim();
          const content = parts[i + 1] || '';
          if (!label || !content.trim()) continue;

          const cleaned = this.stripEmptyBoldArtifacts(this.normalizeHeadingBreaks(content));
          sections.push({
            label,
            paragraphs: this.splitParagraphs(cleaned),
          });
        }

        if (sections.length > 0) return sections;
      }

      const parts = raw.split(/\n?HALAMAN\s+(\d+)\n?/);
      const sections = [];

      if (parts[0]?.trim()) {
        const cleaned = this.stripEmptyBoldArtifacts(this.normalizeHeadingBreaks(parts[0]));
        sections.push({
          label: 'Ringkasan',
          paragraphs: this.splitParagraphs(cleaned),
        });
      }

      for (let i = 1; i < parts.length; i += 2) {
        const pageNum = parts[i];
        const content = parts[i + 1] || '';
        if (!content.trim()) continue;

        const firstLine = this.getCleanLabel(content);
        const cleaned = this.stripEmptyBoldArtifacts(this.normalizeHeadingBreaks(content));
        sections.push({
          label: `${pageNum}. ${firstLine}`,
          paragraphs: this.splitParagraphs(cleaned),
        });
      }

      return sections;
    },
    blogSectionData() {
      return this.landingSections.find(s => s.section_key === 'blog') || null;
    },
    blogHeadlineItem() {
      const section = this.blogSectionData;
      if (!section?.items) return null;
      return section.items.find(i => i.block_item_id === null && i.title === 'BLOG') || null;
    },
    blogItems() {
      const section = this.blogSectionData;
      const headline = this.blogHeadlineItem;
      if (!section?.items || !headline) return [];

      return section.items
        .filter(i => i.block_item_id === headline.id && i.is_active)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
  },
  methods: {
    getBlockVariant(section, block, children) {
      if (section.section_key === 'blog' && block.title === 'BLOG') return 'blog';
      if (section.section_key === 'solutions') return 'solution';

      const map = {
        'PRODUCT OVERVIEW': 'overview',
        'WHY CHOOSE US': 'why',
        'NEWSLETTER': 'newsletter',
        'OUR TEAMS': 'team',
        'FREQUENTLY ASKED QUESTIONS': 'faq',
        'EXPERIENCE': 'experience',
      };
      const byTitle = map[(block.title || '').trim().toUpperCase()];
      if (byTitle) return byTitle;

      if (children.some(c => c.pdf)) return 'newsletter';
      if (children.some(c => c.role)) return 'team';

      if (children.length) {
        const hasChildTitle = children.some(c => c.title && c.title.trim() !== '');
        return hasChildTitle ? 'technology' : 'features';
      }

      return 'overview';
    },
    parseBlockImages(image) {
      if (!image) return [];
      try {
        const parsed = JSON.parse(image);
        return Array.isArray(parsed) ? parsed : [image];
      } catch {
        return [image];
      }
    },
    requestEditBlog(item) {
      if (!item) return;
      this.pendingEditItem = item;
      this.blogVerifyDialogOpen = true;
    },
    async onBlogVerified({ token }) {
      this.blogEditToken = token;

      if (this.pendingEditItem) {
        this.editBlog(this.pendingEditItem);
        this.pendingEditItem = null;
      }
    },
    editBlog(item) {
      if (!item) return;

      this.blogEditDialogInitialData = {
        id: item.id,
        title: item.title || "",
        text: item.text || "",
        imageUrl: item.image ? `${this.baseURL}/${item.image}` : null,
        sections: this.plainToBlogSections(item.pdf_content)
      };

      this.blogEditDialog = true;
    },
    closeBlogEditor() {
      this.blogEditDialogInitialData = {};
    },
    async saveBlogEdit(payload) {
      this.blogEditorSaving = true;

      try {
        const combinedContent = this.combineSections(payload.sections);

        const fd = new FormData();
        fd.append('title', payload.title ?? '');
        fd.append('text', payload.text ?? '');
        fd.append('pdf_content', combinedContent);

        if (payload.imageFile) {
          fd.append('image', payload.imageFile);
        }
        if (payload.imageRemoved) {
          fd.append('remove_image', '1');
        }

        const res = await axios.put(
          `landing-page-items/${payload.id}`,
          fd,
          {
            headers: {
              'auth-token': this.blogEditToken
            }
          }
        );

        if (res.data.success) {
          Object.assign(this.selectedBlogItem, res.data.data);

          this.blogEditDialog = false;

          await this.fetchLandingSections();
        }
      } catch (err) {
        console.log(err);
        this.showToast?.(err?.response?.data?.message || 'Failed to update blog', 'error');
      } finally {
        this.blogEditorSaving = false;
      }
    },
    combineSections(sections) {
      return sections
        .map(section => {
          const plainText = this.htmlToPlainMarkup(section.content);
          if (!plainText) return '';
          const label = (section.label || 'Content').trim();
          return `###SECTION:${label}###\n${plainText}`;
        })
        .filter(text => text.length > 0)
        .join('\n\n');
    },
    async handleInlineImageUpload(file, Editor, cursorLocation, resetUploader) {
      try {
        const fd = new FormData();
        fd.append('image', file);

        const res = await axios.post('landing-page-upload-inline-image', fd);

        if (res.data.success) {
          const url = `${this.baseURL}/${res.data.data.path}`;
          Editor.insertEmbed(cursorLocation, 'image', url);
          resetUploader();
        } else {
          this.showToast?.('Gagal upload gambar', 'error');
        }
      } catch (err) {
        this.showToast?.(err?.response?.data?.message || 'Gagal upload gambar', 'error');
      }
    },
    htmlToPlainMarkup(html) {
      if (!html) return '';

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const output = [];
      let buffer = '';

      const pushLine = (line) => {
        const trimmed = line.trim();
        if (trimmed) output.push(trimmed);
      };

      const flushBuffer = () => {
        pushLine(buffer);
        buffer = '';
      };

      const wrap = (text, marks) => {
        if (!text) return text;
        let result = text;
        if (marks.strike) result = `~~${result}~~`;
        if (marks.underline) result = `__${result}__`;
        if (marks.italic) result = `*${result}*`;
        if (marks.bold) result = `**${result}**`;
        return result;
      };

      const getAlign = (node) => {
        const className = node.className || '';
        if (/ql-align-center/.test(className)) return 'center';
        if (/ql-align-right/.test(className)) return 'right';
        if (/ql-align-justify/.test(className)) return 'justify';

        const style = node.getAttribute?.('style') || '';
        const match = style.match(/text-align\s*:\s*(center|right|justify)/i);
        if (match) return match[1].toLowerCase();

        return null;
      };

      const walk = (node, marks) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const raw = node.textContent.replace(/\s+/g, ' ');
          if (raw) buffer += wrap(raw, marks);
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        let nextMarks = marks;

        switch (tag) {
          case 'strong': case 'b':
            nextMarks = { ...marks, bold: true }; break;
          case 'em': case 'i':
            nextMarks = { ...marks, italic: true }; break;
          case 'u':
            nextMarks = { ...marks, underline: true }; break;
          case 's': case 'strike': case 'del':
            nextMarks = { ...marks, strike: true }; break;

          case 'br':
            buffer += '\n';
            return;

          case 'img': {
            const src = node.getAttribute('src') || '';
            flushBuffer();
            pushLine(`###IMAGE:${src}###`);
            return;
          }

          case 'a': {
            const href = node.getAttribute('href') || '';
            const label = node.textContent.replace(/\s+/g, ' ').trim();
            buffer += `${wrap(label, marks)} (${href})`;
            return;
          }

          case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': {
            flushBuffer();
            node.childNodes.forEach(child => walk(child, marks));
            const content = buffer.trim();
            buffer = '';
            if (content) {
              const level = Math.min(3, parseInt(tag[1], 10));
              const align = getAlign(node);
              const prefix = align ? `@ALIGN=${align}@` : '';
              pushLine(`${prefix}${'#'.repeat(level)} ${content}`);
            }
            return;
          }

          case 'li': {
            const isOrdered = node.parentElement?.tagName.toLowerCase() === 'ol';
            flushBuffer();
            node.childNodes.forEach(child => walk(child, marks));
            const content = buffer.trim();
            buffer = '';
            if (content) {
              if (isOrdered) {
                const siblings = Array.from(node.parentElement.children).filter(
                  el => el.tagName.toLowerCase() === 'li'
                );
                const index = siblings.indexOf(node) + 1;
                pushLine(`${index}. ${content}`);
              } else {
                pushLine(`• ${content}`);
              }
            }
            return;
          }

          case 'p': case 'div': {
            flushBuffer();
            node.childNodes.forEach(child => walk(child, marks));
            const content = buffer.trim();
            buffer = '';
            if (content) {
              const align = getAlign(node);
              const prefix = align ? `@ALIGN=${align}@` : '';
              pushLine(`${prefix}${content}`);
            }
            return;
          }

          default:
            break;
        }

        node.childNodes.forEach(child => walk(child, nextMarks));
      };

      doc.body.childNodes.forEach(node => walk(node, {}));
      flushBuffer();

      return output.join('\n');
    },
    plainToBlogSections(pdfContent) {
      if (!pdfContent) return [];

      const parts = pdfContent.split(/###SECTION:(.*?)###/g);
      const sections = [];
      for (let i = 1; i < parts.length; i += 2) {
        const label = (parts[i] || '').trim();
        const plain = (parts[i + 1] || '').trim();
        sections.push({
          label,
          content: this.plainMarkupToHtml(plain)
        });
      }
      return sections;
    },
    plainMarkupToHtml(text) {
      if (!text) return '';

      let raw = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

      raw = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      raw = raw.replace(/###IMAGE:(.*?)###/g, (match, src) => `\n<p><img src="${src}"></p>\n`);

      const lines = raw.split('\n');
      const htmlParts = [];

      let listBuffer = [];
      let listType = null;

      const flushList = () => {
        if (listBuffer.length === 0) return;
        const items = listBuffer.map(item => `<li>${item}</li>`).join('');
        htmlParts.push(`<${listType}>${items}</${listType}>`);
        listBuffer = [];
        listType = null;
      };

      const applyInline = (str) => {
        let out = str;
        out = out.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        out = out.replace(/__(.*?)__/g, '<u>$1</u>');
        out = out.replace(/~~(.*?)~~/g, '<s>$1</s>');
        out = out.replace(/\*(.*?)\*/g, '<em>$1</em>');
        return out;
      };

      const extractAlign = (trimmed) => {
        const m = trimmed.match(/^@ALIGN=(center|right|justify)@(.*)$/);
        if (m) return { align: m[1], rest: m[2] };
        return { align: null, rest: trimmed };
      };

      for (const rawLine of lines) {
        let trimmed = rawLine.trim();

        if (!trimmed) {
          flushList();
          continue;
        }

        if (/^<(p|img)/i.test(trimmed)) {
          flushList();
          htmlParts.push(trimmed);
          continue;
        }

        const { align, rest } = extractAlign(trimmed);
        trimmed = rest;
        const alignAttr = align ? ` style="text-align:${align}"` : '';

        const numberedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
        const bulletMatch = trimmed.match(/^•\s+(.*)$/);

        if (numberedMatch) {
          if (listType !== 'ol') flushList();
          listType = 'ol';
          listBuffer.push(applyInline(numberedMatch[1]));
          continue;
        }

        if (bulletMatch) {
          if (listType !== 'ul') flushList();
          listType = 'ul';
          listBuffer.push(applyInline(bulletMatch[1]));
          continue;
        }

        flushList();

        if (/^###\s+/.test(trimmed)) {
          htmlParts.push(`<h3${alignAttr}>${applyInline(trimmed.replace(/^###\s+/, ''))}</h3>`);
        } else if (/^##\s+/.test(trimmed)) {
          htmlParts.push(`<h2${alignAttr}>${applyInline(trimmed.replace(/^##\s+/, ''))}</h2>`);
        } else if (/^#\s+/.test(trimmed)) {
          htmlParts.push(`<h1${alignAttr}>${applyInline(trimmed.replace(/^#\s+/, ''))}</h1>`);
        } else {
          htmlParts.push(`<p${alignAttr}>${applyInline(trimmed)}</p>`);
        }
      }

      flushList();

      return htmlParts.join('');
    },
    renderBlogParagraph(text) {
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      const normalized = escaped.replace(/\*\*([^*]+)\*\*/g, (match, boldContent) => {
        const splitMatch = boldContent.match(/^(.*?[a-z])([A-Z].*)$/);
        if (!splitMatch) return match;

        const [, before, after] = splitMatch;
        const looksLikeGluedHeading = before.trim().length > 0 && before.trim().length <= 60;
        if (!looksLikeGluedHeading) return match;

        return `**${before.trim()}**@@SPLIT@@**${after.trim()}**`;
      });

      const parts = normalized.split('@@SPLIT@@');

      return parts
        .map(part => {
          let workingPart = part;

          const alignMatch = workingPart.match(/^@ALIGN=(center|right|justify)@(.*)$/);
          const align = alignMatch ? alignMatch[1] : null;
          if (alignMatch) workingPart = alignMatch[2];

          const h3Match = workingPart.match(/^###\s+(.+)$/);
          const h2Match = workingPart.match(/^##\s+(.+)$/);
          const h1Match = workingPart.match(/^#\s+(.+)$/);

          const headingMatch = h3Match || h2Match || h1Match;
          const headingTag = h3Match ? 'h3' : h2Match ? 'h2' : h1Match ? 'h1' : null;

          let html;
          if (headingMatch && headingTag) {
            html = headingMatch[1];
          } else {
            html = workingPart;
          }

          html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
          html = html.replace(/__(.+?)__/g, '<u>$1</u>');
          html = html.replace(/~~(.+?)~~/g, '<s>$1</s>');
          html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
          html = html.replace(/###IMAGE:(.+?)###/g, (match, src) => {
            return `<img src="${src}" alt="Blog image" class="blog-inline-image" />`;
          });

          const styleAttr = align ? ` style="text-align:${align}"` : '';

          if (headingTag) {
            return `<${headingTag} class="blog-${headingTag}"${styleAttr}>${html}</${headingTag}>`;
          }
          return `<p${styleAttr}>${html}</p>`;
        })
        .join('');
    },
    getCleanLabel(content, maxLen = 45) {
      let line = content.trim().split('\n')[0] || '';
      line = line.replace(/\*\*/g, '').trim();
      line = line.replace(/^[\s•\-*–·]+/, '').trim();

      if (line.length <= maxLen) return line;

      const truncated = line.substring(0, maxLen);
      const lastSpace = truncated.lastIndexOf(' ');
      const clean = lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated;

      return clean + '…';
    },
    formatBlogDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    splitParagraphs(text) {
      return text
        .split(/\n{1,}/)
        .map(p => p.trim())
        .filter(p => p.length > 0);
    },
    normalizeHeadingBreaks(text) {
      if (!text) return text;
      let result = text;
      result = result.replace(/([.!?%)])(\*\*[^*]+\*\*)/g, '$1\n\n$2');
      result = result.replace(/(\*\*[^*]+\*\*)\n(?!\n)/g, '$1 ');
      return result;
    },
    stripEmptyBoldArtifacts(text) {
      if (!text) return text;
      return text
        .replace(/\*\*\s*\*\*/g, '')
        .split('\n')
        .filter(line => {
          const trimmed = line.trim();
          return trimmed !== '****' && trimmed !== '**';
        })
        .join('\n');
    },
    openBlogDetail(item) {
      this.selectedBlogItem = item;
      this.blogDetailOpen = true;
      this.activeBlogSectionIndex = 0;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    closeBlogDetail() {
      this.blogDetailOpen = false;
      this.selectedBlogItem = null;
    },
    truncateTitle(title, length = 30) {
      if (!title) return '';
      return title.length > length ? title.substring(0, length) + '...' : title;
    },
    getProductImages(item) {
      if (!item.image) return [];
      let parsed;
      try {
        parsed = JSON.parse(item.image);
      } catch {
        return [item.image];
      }
      return Array.isArray(parsed) ? parsed : [parsed];
    },
    prevProductImage(item) {
      const currentIndex = this.getActiveImageIndex(item.id);
      if (currentIndex > 0) {
        this.setActiveImageIndex(item.id, currentIndex - 1);
      }
    },
    nextProductImage(item) {
      const images = this.getProductImages(item);
      const currentIndex = this.getActiveImageIndex(item.id);
      if (currentIndex < images.length - 1) {
        this.setActiveImageIndex(item.id, currentIndex + 1);
      }
    },
    getActiveImageIndex(itemId) {
      return this.activeOverviewImageIndex[itemId] ?? 0;
    },
    setActiveImageIndex(itemId, idx) {
      this.$set(this.activeOverviewImageIndex, itemId, idx);
    },
    getCardScrollStep() {
      const grid = this.$refs.newsletterGrid;
      const card = this.$refs.newsletterCards && this.$refs.newsletterCards[0];
      if (!grid || !card) return 0;

      const style = getComputedStyle(grid);
      const gap = parseFloat(style.columnGap || style.gap || 0);

      const cardWidth = card.offsetWidth + gap;
      return cardWidth * this.visibleCardNewsletterCount;
    },
    scrollNewsletter(direction) {
      const grid = this.$refs.newsletterGrid;
      if (!grid) return;

      const step = this.getCardScrollStep();
      const amount = direction === 'next' ? step : -step;

      grid.scrollBy({ left: amount, behavior: 'smooth' });
    },
    onNewsletterScroll() {
      this.updateArrowState();
    },
    updateArrowState() {
      const grid = this.$refs.newsletterGrid;
      if (!grid) return;

      const tolerance = 4;
      this.canScrollPrev = grid.scrollLeft > tolerance;
      this.canScrollNext = grid.scrollLeft < grid.scrollWidth - grid.clientWidth - tolerance;
    },
    viewNewsletter(item) {
      if (!item.pdf) return;

      this.selectedTitlePDF = item.title;

      if (
        this.selectedPDF &&
        typeof this.selectedPDF === 'string' &&
        this.selectedPDF.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(this.selectedPDF);
        } catch (e) {
          console.warn('Failed to revoke object URL:', e);
        }
      }

      this.selectedPDF = `${this.baseURL}/${item.pdf}`;
      this.dialogViewPDF = true;
    },
    getHeadlineItem(section, title) {
      if (!section) return null;
      return section.items.find(i => i.block_item_id === null && i.title === title) || null;
    },
    getChildItems(section, headlineTitle) {
      const headline = this.getHeadlineItem(section, headlineTitle);
      if (!headline) return [];

      return section.items
        .filter(i => i.block_item_id === headline.id)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    getChildItemsById(section, headlineId) {
      if (!section) return [];
      return section.items
        .filter(i => i.block_item_id === headlineId)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    getRootItems(section) {
      if (!section) return [];
      return section.items
        .filter(i => i.block_item_id === null)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    goHome() {
      const firstSection = this.landingSections[0];
      if (firstSection) {
        this.showSection(firstSection.section_key);
      }
    },
    getFaqHeadline(section) {
      if (!section) return null;
      return section.items.find(i => i.block_item_id === null && i.title === 'FAQ') || null;
    },
    getFaqItems(section) {
      const faqItem = this.getFaqHeadline(section);
      if (!faqItem) return [];

      return section.items
        .filter(i => i.block_item_id === faqItem.id)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    async loadAssetsTheme() {
      try {
        const appTitle = window.APP_CONFIG?.VUE_APP_TITLE || process.env.VUE_APP_TITLE;
        const assetsModule =
          appTitle === 'Websitemedan'
            ? await import('@/constants/asset-logo-MedAn.js')
            : await import('@/constants/asset-logo-Garuda.js')
        this.logo = assetsModule.LOGO_WEB;
      } catch (error) {
        console.error('Failed to load environment assets:', error);
      }
    },
    async fetchNavSections() {
      try {
        const response = await axios.get('landing-page-sections/nav');
        if (response.data.success) {
          this.navSections = response.data.data;
        }
      } catch (err) {
        console.error('Failed to load nav sections:', err);
      }
    },
    async fetchLandingSections() {
      this.sectionsLoading = true;
      this.sectionsError = null;
      try {
        const response = await axios.get('landing-page-sections');
        if (response.data.success) {
          this.landingSections = response.data.data;
        } else {
          throw new Error('Gagal memuat data sections.');
        }
      } catch (err) {
        this.sectionsError = err?.response?.data?.message || err.message || 'Terjadi kesalahan.';
      } finally {
        this.sectionsLoading = false;
      }
    },
    getSectionByKey(key) {
      return this.landingSections.find((s) => s.section_key === key) || null;
    },
    showSection(section) {
      this.currentSection = section;
      this.mobileMenuOpen = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    handleLogin() {
      this.$router.push('/login');
    },
    handleRequestDemo() {
      console.log('handleRequestDemo');
    },
    handleStartDemo() {
      console.log('handleStartDemo');
    },
    handleLearnMore() {
      console.log('handleLearnMore');
    },
    handleSubmit() {
      console.log('Form submitted:', this.form);
    },
  },
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.landing-page {
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #1a1a2e;
  background-color: #F2F4F8;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  background-color: #ffffff;
  border-bottom: 1px solid #e8edf3;
  z-index: 100;
  flex-shrink: 0;
  position: sticky;
  top: 0;
}

.navbar-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  position: relative;
}

.navbar-brand {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.navbar-brand img {
  height: var(--logo-height-login, 36px);
  width: var(--logo-width-login, auto);
  object-fit: contain;
}

.navbar-menu {
  display: flex;
  list-style: none;
  gap: 4px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.nav-link {
  text-decoration: none;
  color: #21272A;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 6px;
  transition: color 0.2s, background-color 0.2s;
}

.nav-link:hover,
.nav-link--active {
  background-color: #B3CCFF;
}

.navbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
}

.btn-login {
  background: transparent;
  border: 1.5px solid #0B6599;
  color: #0B6599;
  font-size: 14px;
  font-weight: 500;
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.btn-login:hover {
  background-color: #0B6599;
  color: #ffffff;
}

.btn-demo {
  background-color: #0B6599;
  border: 1.5px solid #0B6599;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: auto;
  z-index: 101;
}

.hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: #4a5568;
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.2s ease;
}

.hamburger.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.hamburger.open span:nth-child(2) {
  opacity: 0;
}

.hamburger.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.mobile-menu {
  display: none;
  flex-direction: column;
  background: #ffffff;
  padding: 12px 24px 20px;
  border-top: 1px solid #e8edf3;
  gap: 4px;
  overflow: hidden;
}

.mobile-menu.open {
  display: flex;
}

.mobile-nav-link {
  text-decoration: none;
  color: #21272A;
  font-size: 15px;
  font-weight: 500;
  padding: 10px 4px;
  border-bottom: 1px solid #f0f4f8;
}

.mobile-nav-link:hover,
.mobile-nav-link--active {
  background-color: #B3CCFF;
  padding-left: 8px;
}

.mobile-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.mobile-actions .btn-login,
.mobile-actions .btn-demo {
  flex: 1;
}

.homepage {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #F2F4F8;
  padding: 60px 24px;
}

.section-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #0B6599;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  color: #ef4444;
  text-align: center;
}

.section-error i {
  font-size: 40px;
}

.section-error button {
  padding: 7px 18px;
  background: transparent;
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 6px;
  cursor: pointer;
}

.homepage-container {
  max-width: 720px;
  width: 100%;
  text-align: center;
}

.homepage-title {
  font-size: clamp(28px, 5vw, 44px);
  font-weight: 700;
  color: #21272A;
  line-height: 1.25;
  letter-spacing: -0.5px;
  margin-bottom: 24px;
}

.homepage-description {
  font-size: 15px;
  color: #21272A;
  line-height: 1.75;
  max-width: 560px;
  margin: 0 auto 30px;
}

.homepage-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.btn-start-demo {
  background-color: #0B6599;
  border: none;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 28px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  letter-spacing: 0.2px;
}

.btn-learn-more {
  background: transparent;
  border: 1.5px solid #0B6599;
  color: #0B6599;
  font-size: 15px;
  font-weight: 500;
  padding: 12px 28px;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.product-overview-section {
  margin-top: 90px;
  text-align: center;
  overflow: visible;
}

.product-overview-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.product-overview-title {
  max-width: 900px;
  margin: 0 auto 40px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
}

.product-overview-wrapper {
  position: relative;
  max-width: 1170px;
  margin: 0 auto;
  padding: 0 60px;
}

.product-overview-placeholder {
  max-width: 1050px;
  height: 500px;
  margin: 0 auto;
  border: 6px solid #DDE1E6;
  border-radius: 24px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #697077;
  font-size: 18px;
  font-weight: 500;
}

.product-overview-placeholder img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.product-overview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.product-overview-wrapper .newsletter-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid #DDE1E6;
  background: #FFFFFF;
  color: #21272A;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.10);
  transition: background .2s, border-color .2s, color .2s, box-shadow .2s, transform .2s;
}

.product-overview-wrapper .newsletter-arrow--prev {
  left: 0;
}

.product-overview-wrapper .newsletter-arrow--next {
  right: 0;
}

.product-overview-wrapper .newsletter-arrow:hover:not(.newsletter-arrow--disabled) {
  background: #001D6C;
  border-color: #001D6C;
  color: #FFFFFF;
  box-shadow: 0 6px 18px rgba(0, 29, 108, 0.25);
  transform: translateY(-50%) scale(1.06);
}

.product-overview-wrapper .newsletter-arrow--disabled {
  opacity: .35;
  cursor: default;
  pointer-events: none;
  box-shadow: none;
}

.product-overview-dots {
  display: flex;
  justify-content: center;
  margin-top: 28px;
  padding-bottom: 40px;
}

.product-overview-dot {
  height: 10px;
  width: 10px;
  margin: 0 6px;
  border-radius: 50%;
  background: #CBD5E1;
  cursor: pointer;
  transition: all 0.3s ease;
}

.product-overview-dot.active {
  background: #0B6599;
  width: 30px;
  border-radius: 5px;
}

.homepage-why-section {
  margin-top: 90px;
  width: 100%;
}

.homepage-why-subtitle {
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: .8px;
  margin-bottom: 12px;
}

.homepage-why-title {
  text-align: center;
  max-width: 900px;
  margin: 0 auto 30px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
}

.homepage-why-grid {
  max-width: 1150px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
}

.homepage-why-item {
  text-align: center;
}

.homepage-why-icon {
  font-size: 42px;
  color: #C1C7CD;
  margin-bottom: 18px;
}

.homepage-why-item h4 {
  font-size: 20px;
  font-weight: 700;
  color: #21272A;
  margin-bottom: 14px;
}

.homepage-why-item p {
  font-size: 14px;
  line-height: 1.7;
  color: #424446;
}

.technology-feature-section {
  margin-top: 90px;
}

.technology-feature-subtitle {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.technology-feature-title {
  text-align: center;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
  margin-bottom: 50px;
}

.technology-feature-content {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.3fr 0.9fr;
  gap: 60px;
  align-items: center;
}

.technology-feature-preview {
  height: 360px;
  border: 6px solid #DDE1E6;
  border-radius: 24px;
  background: #F2F4F8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #697077;
  font-size: 18px;
  font-weight: 500;
}

.technology-feature-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.technology-feature-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 45px 40px;
}

.technology-feature-item {
  text-align: left;
}

.technology-feature-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}

.feature-tech-icon {
  font-size: 36px;
  line-height: 1;
  color: #C1C7CD;
  margin: 0;
  flex: 0 0 auto;
}

.technology-feature-item-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
  color: #21272A;
}

.technology-feature-item:hover .feature-tech-icon-wrap {
  background: #DCE4FF;
  transform: translateY(-2px);
}

.technology-feature-item p {
  font-size: 15px;
  line-height: 1.7;
  color: #424446;
  margin: 0;
}

.newsletter-section {
  background: #F2F4F8;
  padding: 90px 24px 0px;
}

.newsletter-container {
  max-width: 1180px;
  margin: 0 auto;
  text-align: center;
  overflow: visible;
}

.newsletter-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: .8px;
  margin-bottom: 12px;
}

.newsletter-title {
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
  margin-bottom: 40px;
}

.newsletter-slider-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 60px;
}

.newsletter-grid {
  display: flex;
  overflow-x: hidden;
  gap: 22px;
  width: 100%;
  margin-bottom: 40px;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
}

.newsletter-card {
  flex: 0 0 calc((100% - 44px) / 3);
  flex-shrink: 0;
  scroll-snap-align: start;
  background: #FFFFFF;
  border: 1px solid #DDE1E6;
  border-radius: 18px;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition: border .2s, box-shadow .2s;
}

.newsletter-card:hover {
  border: 1.5px dashed #0B6599;
  box-shadow: 0 0 0 3px rgba(4, 113, 165, 0.1);
}

.newsletter-card--disabled {
  cursor: default;
}

.newsletter-card--disabled:hover {
  border-color: transparent;
  box-shadow: none;
}

.newsletter-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(calc(-50% - 20px));
  z-index: 2;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid #DDE1E6;
  background: #FFFFFF;
  color: #21272A;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.10);
  transition: background .2s, border-color .2s, color .2s, box-shadow .2s, transform .2s;
}

.newsletter-arrow--prev {
  left: 0;
}

.newsletter-arrow--next {
  right: 0;
}

.newsletter-arrow:hover:not(.newsletter-arrow--disabled) {
  background: #001D6C;
  border-color: #001D6C;
  color: #FFFFFF;
  box-shadow: 0 6px 18px rgba(230, 21, 63, 0.25);
  transform: translateY(calc(-50% - 20px)) scale(1.06);
}

.newsletter-arrow--disabled {
  opacity: .35;
  cursor: default;
  pointer-events: none;
  box-shadow: none;
}

.newsletter-image {
  margin: 15px;
  height: 230px;
  border-radius: 12px;
  overflow: hidden;
  background: #F2F4F8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #697077;
  font-size: 15px;
  font-weight: 500;
  position: relative;
}

.newsletter-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 35%;
  display: block;
  transition: transform .4s ease;
}

.newsletter-card:hover .newsletter-image img {
  transform: scale(1.04);
}

.newsletter-card-title {
  padding: 1px 16px 4px;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.45;
  color: #21272A;
  min-height: 20px;
}

.newsletter-readmore {
  display: inline-block;
  padding: 0 16px 18px;
  font-size: 14px;
  font-weight: 400;
  color: #0B6599;
  text-decoration: none;
}

.newsletter-readmore--disabled {
  color: #697077;
  cursor: default;
  pointer-events: none;
}

.team-section {
  width: 100%;
  margin-top: 80px;
  padding: 70px 40px;
  background: #F2F4F8;
}

.team-subtitle {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.team-title {
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  color: #21272A;
  margin-bottom: 40px;
  line-height: 1.3;
}

.team-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.team-card {
  text-align: center;
}

.team-image {
  width: 100%;
  height: 320px;
  object-fit: cover;
  display: block;
}

.team-image-placeholder {
  width: 100%;
  height: 320px;
  border: 6px solid #DDE1E6;
  border-radius: 24px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #697077;
  font-size: 18px;
  font-weight: 500;
}

.team-name {
  margin-top: 14px;
  font-size: 14px;
  font-weight: 500;
  color: #21272A;
}

.team-role {
  margin-top: 4px;
  font-size: 13px;
  color: #697077;
}

.faq-section {
  background: #F2F4F8;
  width: 100%;
  padding: 45px 80px;
}

.faq-subtitle {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: .8px;
  margin-bottom: 12px;
}

.faq-title {
  text-align: center;
  font-size: 42px;
  font-weight: 700;
  color: #21272A;
  margin-bottom: 40px;
  line-height: 1.2;
}

.faq-container {
  max-width: 900px;
  margin: 0 auto;
}

.faq-item {
  background: #FFFFFF;
  border: 1px solid #DDE1E6;
  margin-bottom: 10px;
}

.faq-item-active .faq-header {
  padding-bottom: 12px;
}

.faq-header {
  padding: 16px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.faq-header span:first-child {
  font-size: 14px;
  font-weight: 600;
  color: #21272A;
}

.faq-icon {
  font-size: 18px;
  color: #21272A;
  font-weight: 400;
}

.faq-content {
  padding: 0 18px 16px;
  font-size: 13px;
  line-height: 1.7;
  color: #525252;
}

.experience-section {
  background: #F2F4F8;
  padding: 90px 24px;
}

.experience-container {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}

.experience-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #21272A;
  line-height: 1.4;
}

.experience-icon {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  transform: translateY(-5px);
}

.experience-icon svg {
  display: block;
}

.experience-btn {
  margin-top: 34px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: #0B6599;
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all .25s ease;
}

.experience-btn i {
  font-size: 18px;
}

.product-section {
  background: #FFFFFF;
  padding: 50px 24px 80px;
}

.product-container {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}

.product-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.product-title {
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
  margin-bottom: 24px;
}

.product-description {
  max-width: 1000px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.8;
  color: #21272A;
}

.features-section {
  margin-top: 90px;
}

.features-subtitle {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: .8px;
  margin-bottom: 12px;
}

.features-title {
  text-align: center;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
  margin-bottom: 60px;
}

.features-content {
  max-width: 1150px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 40px;
  align-items: center;
}

.features-preview {
  height: 320px;
  border: 6px solid #DDE1E6;
  border-radius: 24px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #697077;
  font-size: 16px;
  font-weight: 500;
}

.features-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.features-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 36px 28px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.feature-icon {
  font-size: 34px;
  color: #C1C7CD;
  flex-shrink: 0;
}

.feature-text {
  font-size: 14px;
  line-height: 1.7;
  color: #21272A;
  margin-left: 4px;
}

.blog-section {
  background: #FFFFFF;
  padding: 50px 24px 80px;
}

.blog-container {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}

.blog-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.blog-title {
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
  margin-bottom: 24px;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: 40px 32px;
  margin-top: 50px;
  text-align: left;
}

.blog-card {
  display: block;
  text-decoration: none;
  color: inherit;
}

.blog-card-image {
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}

.blog-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.blog-card:hover .blog-card-image img {
  transform: scale(1.05);
}

.blog-card-date {
  font-size: 13px;
  font-weight: 600;
  color: #697077;
  margin-bottom: 8px;
}

.blog-card-title {
  font-size: 19px;
  font-weight: 700;
  color: #21272A;
  line-height: 1.4;
  margin: 0;
}

.blog-card--disabled {
  cursor: default;
  pointer-events: none;
}

.blog-card-image--placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F2F4F8;
  color: #697077;
  font-size: 15px;
  font-weight: 500;
  border-radius: 12px;
}

.blog-detail {
  padding: 0px 24px 80px;
}

.blog-detail-container {
  max-width: 1100px;
  margin: 0 auto;
  text-align: left;
}

.blog-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: #697077;
  margin-bottom: 24px;
}

.blog-breadcrumb i {
  font-size: 18px;
  cursor: pointer;
}

.blog-breadcrumb span {
  cursor: pointer;
}

.blog-breadcrumb-current {
  color: #21272A;
  font-weight: 600;
  cursor: default !important;
}

.blog-detail-title {
  font-size: 52px;
  font-weight: 800;
  color: #21272A;
  line-height: 1.15;
  margin-bottom: 20px;
}

.blog-detail-subtitle {
  font-size: 19px;
  color: #4D5358;
  line-height: 1.6;
  max-width: 850px;
  margin-bottom: 20px;
}

.blog-detail-meta {
  font-size: 16px;
  color: #697077;
  margin-bottom: 40px;
}

.blog-detail-meta-dot {
  margin: 0 8px;
}

.blog-detail-hero {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  margin-bottom: 40px;
}

.blog-detail-author {
  flex: 0 0 120px;
  text-align: center;
}

.blog-detail-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f4f8;
}

.blog-detail-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-detail-avatar--placeholder {
  width: 100%;
  height: 100%;
  background: #F2F4F8;
}

.blog-detail-author-name {
  font-weight: 700;
  color: #21272A;
  font-size: 17px;
}

.blog-detail-image {
  flex: 1;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 16 / 8;
}

.blog-detail-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-detail-body {
  display: flex;
  gap: 48px;
  align-items: flex-start;
}

.blog-detail-toc {
  flex: 0 0 260px;
  position: sticky;
  top: 24px;
}

.blog-detail-toc-label {
  font-size: 18px;
  font-weight: 700;
  color: #21272A;
  letter-spacing: 0.6px;
  margin-bottom: 12px;
}

.blog-detail-toc ul {
  list-style: none;
  padding: 0;
  margin: 0;
  border-left: 2px solid #E4E7EB;
}

.blog-detail-toc li {
  padding: 8px 0 8px 16px;
  font-size: 16px;
  color: #4D5358;
  cursor: pointer;
  transition: color 0.2s ease;
}

.blog-detail-toc li:hover {
  color: #21272A;
}

.blog-detail-toc li.active {
  color: #21272A;
  font-weight: 700;
  border-left: 2px solid #21272A;
  margin-left: -2px;
  padding-left: 16px;
}

.blog-detail-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
  font-size: 18px;
  line-height: 1.8;
  color: #21272A;
}

.blog-detail-content strong.blog-h1 {
  display: block;
  font-size: 28px;
  line-height: 1.3;
  margin-bottom: 4px;
}

.blog-detail-content strong.blog-h2 {
  display: block;
  font-size: 24px;
  line-height: 1.3;
  margin-bottom: 4px;
}

.blog-detail-content strong.blog-h3 {
  display: block;
  font-size: 20px;
  line-height: 1.3;
  margin-bottom: 4px;
}

.blog-detail-content img,
.blog-detail-content .blog-inline-image {
  max-width: 100%;
  width: 100%;
  height: auto;
  display: block;
  margin: 16px auto;
  border-radius: 10px;
  object-fit: contain;
}

.edit-blog-floating {
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 17px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: #111827;
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  box-shadow:
    0 8px 24px rgba(17, 24, 39, 0.18),
    0 2px 6px rgba(17, 24, 39, 0.12);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.edit-blog-floating i {
  font-size: 17px;
  line-height: 1;
  opacity: 0.95;
}

.edit-blog-floating:active {
  transform: translateY(0);
  box-shadow:
    0 5px 14px rgba(17, 24, 39, 0.18);
}

.edit-blog-floating:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.6);
  outline-offset: 3px;
}

.blog-inline-image {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin: 12px 0;
  display: block;
}

.solutions-section {
  background: #FFFFFF;
  padding: 45px 24px;
}

.solutions-container {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}

.solutions-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.solutions-title {
  width: 850px;
  margin: 0 auto 50px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.25;
  color: #21272A;
}

.solutions-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 320px));
  row-gap: 70px;
  column-gap: 80px;
  justify-content: center;
  margin: 0 auto 18px;
}

.solution-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.solution-icon {
  margin-bottom: 5px;
}

.solution-item h3 {
  font-size: 28px;
  font-weight: 500;
  color: #21272A;
  line-height: 1.4;
  max-width: 300px;
}

.technology-section {
  background: #FFFFFF;
  padding: 23px 24px 90px;
}

.technology-container {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}

.technology-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.technology-title {
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #21272A;
  margin-bottom: 24px;
}

.technology-description {
  max-width: 1000px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.8;
  color: #21272A;
}

.technology-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.technology-btn {
  padding: 12px 32px;
  margin-top: 30px;
  background: #0B6599;
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
}

.about-section {
  background: #ffffff;
  padding-top: 75px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.about-container {
  max-width: 860px;
  width: 100%;
  text-align: center;
}

.about-subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #001D6C;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
}

.about-title {
  font-size: 28px;
  font-weight: 700;
  color: #21272A;
  margin-bottom: 24px;
  line-height: 1.3;
}

.about-description {
  font-size: 14px;
  color: #21272A;
  line-height: 1.7;
  margin: 0 auto;
}

.contact-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #F2F4F8;
  padding: 48px 24px 64px;
  gap: 24px;
}

.contact-banner {
  position: relative;
  width: 100%;
  max-width: 680px;
  background: #0B6599;
  border-radius: 16px;
  padding: 36px 40px;
  text-align: center;
  overflow: hidden;
  flex-shrink: 0;
}

.contact-banner-blob {
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #1B9D9F;
}

.contact-banner-blob--left {
  width: 110px;
  height: 110px;
  top: 10px;
  left: -32px;
  background: linear-gradient(180deg, #1B9D9F 0%, #0B6599 100%);
}

.contact-banner-blob--left-sm {
  width: 50px;
  height: 50px;
  top: 80px;
  left: 32px;
  transform: rotate(-120deg);
  background: linear-gradient(180deg, #1B9D9F 0%, #0B6599 100%);
}

.contact-banner-blob--right-lg {
  width: 90px;
  height: 90px;
  bottom: -10px;
  right: -20px;
  background: linear-gradient(180deg, #1B9D9F 0%, #0B6599 100%);
  border-radius: 50%;
}

.contact-banner-blob--right-sm {
  width: 50px;
  height: 50px;
  bottom: -10px;
  right: 40px;
  background: linear-gradient(180deg, #0B6599 0%, #1B9D9F 100%);
  border-radius: 50%;
  transform: rotate(90deg);
}

.contact-banner-content {
  position: relative;
  z-index: 1;
}

.contact-banner-title {
  font-size: 28px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 12px;
}

.contact-banner-description {
  font-size: 13.5px;
  color: #F2F2F2;
  line-height: 1.65;
}

.contact-card {
  width: 100%;
  max-width: 680px;
  background: #FFFFFF;
  border-radius: 16px;
  padding: 32px 36px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
  flex-shrink: 0;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.form-label {
  font-size: 13.5px;
  font-weight: 600;
  color: #21272A;
}

.required {
  color: #e53e3e;
  margin-left: 2px;
}

.form-input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #B3B3B3;
  border-radius: 8px;
  font-size: 14px;
  color: #21272A;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
}

.form-input::placeholder {
  color: #6B6B6B;
}

.form-input:focus {
  border-color: #0B6599;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.1);
}

.form-row {
  display: flex;
  gap: 16px;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.form-select {
  height: 40px;
  padding: 0 36px 0 12px;
  border: 1px solid #B3B3B3;
  border-radius: 8px;
  font-size: 14px;
  color: #21272A;
  background: #ffffff;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-select:focus {
  border-color: #0B6599;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.1);
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6B6B6B;
  font-size: 18px;
  pointer-events: none;
}

.btn-submit {
  width: 100%;
  height: 44px;
  background-color: #0B6599;
  border: none;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
}

.site-footer {
  background-color: #0B6599;
  padding: 40px 24px 32px;
  flex-shrink: 0;
  margin-top: auto;
}

.footer-container {
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  gap: 40px;
  align-items: flex-start;
}

.footer-brand {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-right: auto;
}

.footer-logo-img {
  height: 36px;
  width: auto;
  object-fit: contain;
}

.footer-copyright {
  font-size: 12px;
  color: #F5F7FA;
  line-height: 1.6;
}

.footer-socials {
  display: flex;
  gap: 12px;
  align-items: center;
}

.footer-social-link {
  color: rgba(255,255,255,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  text-decoration: none;
  line-height: 1;
}

.footer-col {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-right: 40px;
}

.footer-col-title {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
}

.footer-links {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  margin: 0;
}

.footer-link {
  text-decoration: none;
  font-size: 13px;
  color: #F5F7FA;
  transition: color 0.2s;
}

.footer-link:hover {
  color: #ffffff;
}

.footer-stay-up-to-date {
  flex: 0 0 200px;
  margin-right: 0;
}

.footer-email-wrap {
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.12);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.2);
}

.footer-email-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 9px 12px;
  font-size: 13px;
  color: #ffffff;
  min-width: 0;
}

.footer-email-input::placeholder {
  color: rgba(255,255,255,0.55);
}

.footer-email-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 9px 12px;
  color: rgba(255,255,255,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

@media (max-width: 1024px) {
  .navbar-menu,
  .navbar-actions {
    display: none;
  }

  .hamburger {
    display: flex;
  }

  .homepage {
    padding: 56px 32px;
  }

  .homepage-description {
    font-size: 15px;
    margin-bottom: 36px;
  }

  .product-overview-section {
    margin-top: 72px;
  }

  .product-overview-title {
    font-size: 34px;
    margin-bottom: 32px;
  }

  .product-overview-wrapper {
    padding: 0 50px;
  }

  .product-overview-placeholder {
    height: 380px;
  }

  .product-overview-wrapper .newsletter-arrow {
    width: 46px;
    height: 46px;
  }

  .product-overview-wrapper .newsletter-arrow svg {
    width: 20px;
    height: 20px;
  }

  .homepage-why-section {
    margin-top: 72px;
  }

  .homepage-why-title {
    font-size: 34px;
    margin-bottom: 28px;
  }

  .homepage-why-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 36px 24px;
  }

  .technology-section {
    padding: 23px 24px 72px;
  }

  .technology-title {
    font-size: 36px;
    margin-bottom: 40px;
  }

  .technology-feature-section {
    margin-top: 72px;
  }

  .technology-feature-title {
    font-size: 34px;
    margin-bottom: 36px;
  }

  .technology-feature-content {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }

  .technology-feature-preview {
    height: 320px;
  }

  .technology-feature-grid {
    gap: 28px 20px;
  }

  .newsletter-section {
    padding: 72px 24px 0;
  }

  .newsletter-title {
    font-size: 30px;
    margin-bottom: 32px;
  }

  .newsletter-slider-wrapper {
    padding: 0 50px;
  }

  .newsletter-grid {
    gap: 20px;
    margin-bottom: 32px;
  }

  .newsletter-card {
    flex: 0 0 calc((100% - 40px) / 3);
  }

  .newsletter-arrow {
    width: 46px;
    height: 46px;
  }

  .newsletter-arrow svg {
    width: 20px;
    height: 20px;
  }

  .newsletter-image {
    height: 200px;
  }

  .about-section {
    padding-top: 72px;
  }

  .about-container {
    padding: 0 32px;
  }

  .about-title {
    font-size: 26px;
    margin-bottom: 24px;
  }

  .about-description {
    font-size: 14px;
  }

  .faq-section {
    padding: 48px 32px;
  }

  .faq-title {
    font-size: 30px;
    margin-bottom: 32px;
  }

  .experience-section {
    padding: 72px 32px;
  }

  .experience-title {
    font-size: 24px;
  }

  .product-section {
    padding: 48px 32px 72px;
  }

  .product-title {
    font-size: 34px;
    margin-bottom: 24px;
  }

  .product-description {
    font-size: 14px;
  }

  .features-section {
    margin-top: 72px;
  }

  .features-title {
    font-size: 34px;
    margin-bottom: 44px;
  }

  .features-content {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }

  .features-preview {
    height: 320px;
  }

  .features-grid {
    gap: 28px 20px;
  }

  .solutions-section {
    padding: 48px 32px;
  }

  .solutions-title {
    max-width: 620px;
    font-size: 28px;
    margin-bottom: 40px;
  }

  .solutions-grid {
    grid-template-columns: repeat(2, minmax(0, 240px));
    justify-content: center;
    row-gap: 44px;
    column-gap: 64px;
  }

  .solution-item h3 {
    font-size: 21px;
  }

  .contact-section {
    padding: 48px 32px 56px;
    gap: 24px;
  }

  .contact-banner,
  .contact-card {
    max-width: 600px;
  }

  .contact-banner-title {
    font-size: 26px;
  }

  .site-footer {
    padding: 36px 32px 28px;
  }

  .footer-container {
    gap: 32px;
  }

  .footer-col {
    margin-right: 24px;
  }
}

@media (max-width: 768px) {
  .navbar-menu,
  .navbar-actions {
    display: none;
  }

  .hamburger {
    display: flex;
  }

  .homepage {
    padding: 48px 20px;
  }

  .homepage-description {
    font-size: 14px;
    margin-bottom: 32px;
  }

  .homepage-actions {
    gap: 12px;
  }

  .btn-start-demo,
  .btn-learn-more {
    padding: 11px 24px;
    font-size: 14px;
  }

  .product-overview-section {
    margin-top: 60px;
  }

  .product-overview-title {
    font-size: 30px;
    margin-bottom: 28px;
    padding: 0 8px;
  }

  .product-overview-wrapper {
    padding: 0 40px;
  }

  .product-overview-placeholder {
    height: 320px;
    border-radius: 16px;
    border-width: 4px;
  }

  .product-overview-wrapper .newsletter-arrow {
    width: 42px;
    height: 42px;
  }

  .product-overview-wrapper .newsletter-arrow svg {
    width: 18px;
    height: 18px;
  }

  .product-overview-dots {
    margin-top: 20px;
    padding-bottom: 28px;
  }

  .homepage-why-section {
    margin-top: 60px;
  }

  .homepage-why-title {
    font-size: 30px;
    margin-bottom: 24px;
    padding: 0 8px;
  }

  .homepage-why-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px 20px;
  }

  .technology-section {
    padding: 20px 20px 60px;
  }

  .technology-title {
    font-size: 30px;
    margin-bottom: 28px;
    padding: 0 8px;
  }

  .technology-btn {
    padding: 11px 24px;
    font-size: 16px;
  }

  .technology-feature-section {
    margin-top: 60px;
  }

  .technology-feature-title {
    font-size: 30px;
    margin-bottom: 32px;
    padding: 0 8px;
  }

  .technology-feature-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .technology-feature-preview {
    height: 280px;
  }

  .technology-feature-grid {
    gap: 28px 20px;
  }

  .newsletter-section {
    padding: 60px 20px 0;
  }

  .newsletter-title {
    font-size: 28px;
    margin-bottom: 28px;
  }

  .newsletter-slider-wrapper {
    padding: 0 40px;
  }

  .newsletter-grid {
    gap: 16px;
    margin-bottom: 28px;
  }

  .newsletter-card {
    flex: 0 0 calc((100% - 16px) / 2);
  }

  .newsletter-arrow {
    width: 42px;
    height: 42px;
  }

  .newsletter-arrow svg {
    width: 18px;
    height: 18px;
  }

  .newsletter-image {
    height: 190px;
  }

  .about-section {
    padding-top: 60px;
  }

  .about-container {
    padding: 0 20px;
  }

  .about-title {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .about-description {
    font-size: 13.5px;
  }

  .team-section {
    margin-top: 60px;
    padding: 50px 24px;
  }

  .team-title {
    font-size: 24px;
    margin-bottom: 28px;
  }

  .team-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .team-image,
  .team-image-placeholder {
    height: 260px;
  }

  .faq-section {
    padding: 40px 24px;
  }

  .faq-title {
    font-size: 28px;
    margin-bottom: 28px;
  }

  .experience-section {
    padding: 60px 20px;
  }

  .experience-title {
    font-size: 22px;
  }

  .product-section {
    padding: 40px 20px 60px;
  }

  .product-title {
    font-size: 30px;
    margin-bottom: 20px;
  }

  .product-description {
    font-size: 13.5px;
  }

  .features-section {
    margin-top: 60px;
  }

  .features-title {
    font-size: 30px;
    margin-bottom: 40px;
    padding: 0 8px;
  }

  .features-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .features-preview {
    height: 280px;
  }

  .features-grid {
    gap: 28px 20px;
  }

  .solutions-section {
    padding: 40px 20px;
  }

  .solutions-title {
    width: 100%;
    max-width: 560px;
    font-size: 26px;
    margin-bottom: 36px;
  }

  .solutions-grid {
    grid-template-columns: repeat(2, minmax(0, 240px));
    row-gap: 44px;
    column-gap: 32px;
  }

  .solution-item h3 {
    font-size: 20px;
    max-width: 220px;
  }

  .contact-section {
    padding: 40px 20px 48px;
    gap: 20px;
  }

  .contact-banner {
    padding: 28px 28px;
  }

  .contact-banner-title {
    font-size: 24px;
    margin-bottom: 10px;
  }

  .contact-banner-description {
    font-size: 13px;
  }

  .contact-card {
    padding: 28px 24px;
  }

  .form-row {
    gap: 12px;
  }

  .site-footer {
    padding: 32px 20px 24px;
  }

  .footer-container {
    flex-wrap: wrap;
    gap: 32px;
  }

  .footer-brand {
    flex: 0 0 100%;
    margin-right: 0;
  }

  .footer-col {
    margin-right: 32px;
  }

  .footer-stay-up-to-date {
    flex: 1 1 200px;
  }
}

@media (max-width: 480px) {
  .homepage {
    padding: 40px 16px;
    min-height: auto;
    align-items: flex-start;
    text-align: left;
  }

  .homepage-subtitle {
    text-align: left;
  }

  .homepage-title {
    font-size: 26px;
    line-height: 1.3;
    margin-bottom: 16px;
    padding: 0;
    text-align: left;
  }

  .homepage-description {
    font-size: 13.5px;
    line-height: 1.65;
    margin-bottom: 28px;
    text-align: left;
  }

  .homepage-actions {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }

  .btn-start-demo,
  .btn-learn-more {
    width: 100%;
    padding: 13px 24px;
    justify-content: center;
  }

  .product-overview-section {
    margin-top: 48px;
    align-items: flex-start;
    text-align: left;
  }

  .product-overview-subtitle {
    padding: 0;
    text-align: left;
  }

  .product-overview-title {
    font-size: 26px;
    line-height: 1.3;
    padding: 0;
    margin-bottom: 20px;
    text-align: left;
  }

  .product-overview-wrapper {
    padding: 0 30px;
  }

  .product-overview-placeholder {
    height: 220px;
    border-radius: 12px;
    font-size: 15px;
  }

  .product-overview-wrapper .newsletter-arrow {
    width: 38px;
    height: 38px;
  }

  .product-overview-wrapper .newsletter-arrow svg {
    width: 16px;
    height: 16px;
  }

  .homepage-why-section {
    margin-top: 48px;
    align-items: flex-start;
    text-align: left;
  }

  .homepage-why-subtitle {
    text-align: left;
  }

  .homepage-why-title {
    font-size: 26px;
    line-height: 1.3;
    padding: 0;
    text-align: left;
  }

  .homepage-why-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .homepage-why-item {
    text-align: left;
  }

  .homepage-why-icon {
    font-size: 28px;
    margin-bottom: 14px;
    text-align: left;
  }

  .homepage-why-item h4 {
    font-size: 17px;
    margin-bottom: 10px;
    text-align: left;
  }

  .homepage-why-item p {
    font-size: 13.5px;
    line-height: 1.65;
    text-align: left;
  }

  .technology-section {
    padding: 16px 16px 48px;
    align-items: flex-start;
    text-align: left;
  }

  .technology-subtitle {
    font-size: 13px;
    text-align: left;
  }

  .technology-title {
    font-size: 24px;
    line-height: 1.3;
    margin-bottom: 20px;
    padding: 0;
    text-align: left;
  }

  .technology-btn {
    display: none;
  }

  .technology-feature-section {
    margin-top: 32px;
  }

  .technology-feature-title {
    display: none;
  }

  .technology-feature-content {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .technology-feature-preview {
    height: 200px;
    border-radius: 12px;
  }

  .technology-feature-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .technology-feature-item {
    text-align: left;
  }

  .technology-feature-head {
    gap: 12px;
    margin-bottom: 10px;
  }

  .feature-tech-icon {
    font-size: 26px;
    margin: 0;
    text-align: left;
  }

  .technology-feature-item-title {
    font-size: 15.5px;
  }

  .technology-feature-item p {
    font-size: 13.5px;
    line-height: 1.65;
    text-align: left;
  }

  .newsletter-section {
    padding: 48px 16px 0;
  }

  .newsletter-subtitle {
    font-size: 13px;
  }

  .newsletter-title {
    font-size: 22px;
    margin-bottom: 24px;
  }

  .newsletter-slider-wrapper {
    padding: 0 30px;
  }

  .newsletter-grid {
    gap: 14px;
    margin-bottom: 24px;
  }

  .newsletter-card {
    flex: 0 0 88%;
  }

  .newsletter-arrow {
    width: 38px;
    height: 38px;
  }

  .newsletter-arrow svg {
    width: 16px;
    height: 16px;
  }

  .newsletter-image {
    height: 170px;
    margin: 12px;
  }

  .newsletter-card-title {
    font-size: 16px;
  }

  .about-section {
    padding-top: 48px;
  }

  .about-container {
    padding: 0 16px;
  }

  .about-subtitle {
    font-size: 13px;
  }

  .about-title {
    font-size: 19px;
    margin-bottom: 16px;
  }

  .about-description {
    font-size: 13px;
    line-height: 1.65;
  }

  .team-section {
    margin-top: 48px;
    padding: 40px 16px;
    align-items: flex-start;
    text-align: left;
  }

  .team-subtitle {
    text-align: left;
  }

  .team-title {
    font-size: 24px;
    line-height: 1.3;
    margin-bottom: 24px;
    padding: 0;
    text-align: left;
  }

  .team-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .team-image,
  .team-image-placeholder {
    height: 260px;
    border-width: 3px;
    border-radius: 14px;
  }

  .team-name {
    text-align: left;
  }

  .team-role {
    text-align: left;
  }

  .faq-section {
    padding: 32px 16px;
  }

  .faq-title {
    font-size: 22px;
    margin-bottom: 24px;
  }

  .faq-header span:first-child {
    font-size: 13px;
  }

  .experience-section {
    padding: 48px 16px;
  }

  .experience-title {
    font-size: 19px;
    gap: 6px;
  }

  .experience-icon svg {
    width: 28px;
    height: 28px;
  }

  .experience-btn {
    width: 100%;
    justify-content: center;
    margin-top: 24px;
  }

  .product-section {
    padding: 32px 16px 40px;
  }

  .product-title {
    font-size: 22px;
    margin-bottom: 16px;
  }

  .product-description {
    font-size: 13px;
    line-height: 1.7;
  }

  .features-section {
    margin-top: 48px;
  }

  .features-title {
    font-size: 22px;
    margin-bottom: 28px;
  }

  .features-preview {
    height: 200px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .feature-icon {
    font-size: 28px;
  }

  .solutions-section {
    padding: 32px 16px;
  }

  .solutions-title {
    font-size: 21px;
    margin-bottom: 28px;
  }

  .solutions-grid {
    grid-template-columns: 1fr;
    row-gap: 32px;
    column-gap: 0;
  }

  .solution-item h3 {
    font-size: 18px;
    max-width: 260px;
  }

  .contact-section {
    padding: 32px 16px 40px;
    gap: 16px;
  }

  .contact-banner {
    padding: 24px 20px;
    border-radius: 12px;
  }

  .contact-banner-title {
    font-size: 20px;
    margin-bottom: 10px;
  }

  .contact-banner-description {
    font-size: 12.5px;
  }

  .contact-banner-blob--left,
  .contact-banner-blob--right-lg {
    width: 70px;
    height: 70px;
  }

  .contact-banner-blob--left-sm,
  .contact-banner-blob--right-sm {
    width: 36px;
    height: 36px;
  }

  .contact-card {
    padding: 20px 16px;
    border-radius: 12px;
  }

  .form-row {
    flex-direction: column;
    gap: 18px;
  }

  .btn-submit {
    height: 42px;
    font-size: 14px;
  }

  .site-footer {
    padding: 32px 16px 24px;
  }

  .footer-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 16px;
    row-gap: 32px;
  }

  .footer-brand {
    grid-column: 1 / -1;
    flex: none;
    margin-right: 0;
    gap: 16px;
  }

  .footer-social-link {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
  }

  .footer-col {
    grid-column: auto;
    margin-right: 0;
    flex: none;
  }

  .footer-col-title {
    font-size: 17px;
    margin-bottom: 4px;
  }

  .footer-links {
    gap: 12px;
  }

  .footer-link {
    font-size: 13.5px;
  }

  .footer-stay-up-to-date {
    grid-column: 1 / -1;
    flex: none;
    width: 100%;
  }
}
</style>